import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Tag, Sparkles, Info, Download, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUserSettings } from '@/hooks/useUserSettings';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import type { SmartCategory, OutlookColor } from '@/types/contact';
import { OUTLOOK_COLORS } from '@/types/contact';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GraphService } from '@/services/graphService';


interface CategoryTemplate {
  name: string;
  color: OutlookColor;
  tags: string[];
  description: string;
}

const CATEGORY_TEMPLATES: CategoryTemplate[] = [
  {
    name: 'Business & Strategy',
    color: 'Blue',
    tags: ['B2B', 'Growth', 'Partnership'],
    description: 'Companies focused on business strategies, partnerships, or growth.'
  },
  {
    name: 'Technology & Innovation',
    color: 'Purple',
    tags: ['AI', 'Software', 'Innovation'],
    description: 'Organizations with a technological or innovative focus.'
  },
  {
    name: 'Finance & Operations',
    color: 'Green',
    tags: ['Finance', 'Process', 'Efficiency'],
    description: 'Organizations focused on finance, administration, or operations.'
  },
  {
    name: 'Marketing & Communication',
    color: 'Orange',
    tags: ['Brand', 'PR', 'Media'],
    description: 'Teams or companies in communication, PR, or brand building.'
  },
  {
    name: 'People & Culture',
    color: 'Red',
    tags: ['HR', 'Culture', 'Leadership'],
    description: 'Contacts from HR, recruiting, leadership, or company culture.'
  },
  {
    name: 'Legal & Compliance',
    color: 'Gray',
    tags: ['Law', 'Contract', 'Policy'],
    description: 'Legal or regulatory topics, internal policies.'
  },
  {
    name: 'Education & Research',
    color: 'Yellow',
    tags: ['Learning', 'Training', 'Knowledge'],
    description: 'Educational institutions, research, or learning platforms.'
  },
  {
    name: 'Public & Non-Profit',
    color: 'DarkRed',
    tags: ['NGO', 'Public', 'Government'],
    description: 'Government agencies, NGOs, or non-profit organizations.'
  },
  {
    name: 'Sales & Customer Success',
    color: 'Teal',
    tags: ['Sales', 'Account', 'Client'],
    description: 'Customer support, sales, or key account management.'
  },
  {
    name: 'Creative & Design',
    color: 'Purple',
    tags: ['Design', 'UX', 'Content'],
    description: 'Creative industries, design agencies, or content production.'
  }
];

export const SmartCategoriesSettings = () => {
  const [categories, setCategories] = useState<SmartCategory[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{id: string, name: string} | null>(null);
  
  // Outlook categories state
  const [outlookCategories, setOutlookCategories] = useState<Array<{ id: string; displayName: string; color: string }>>([]);
  const [isLoadingOutlookCategories, setIsLoadingOutlookCategories] = useState(false);
  
  const { 
    enableCategories, 
    autoRunCategories, 
    toggleCategories, 
    toggleAutoRun,
    toggleCategoryActive,
    categories: loadedCategories,
    activeCategories,
    isLoading: settingsLoading,
    refresh
  } = useUserSettings();

  const [formData, setFormData] = useState({
    name: '',
    color: 'Blue' as OutlookColor,
    tags: ['', '', ''],
    description: '',
    createInOutlook: false
  });

  useEffect(() => {
    setCategories(loadedCategories);
  }, [loadedCategories]);

  const loadCategories = async () => {
    // Categories are now loaded via hook
  };

  const loadOutlookCategories = async () => {
    setIsLoadingOutlookCategories(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({ title: 'Authentication required', variant: 'destructive' });
        return;
      }

      // 1. Load Outlook categories
      const outlookCats = await GraphService.getCategories();
      
      // 2. Check which categories already exist
      const { data: existingCategories } = await supabase
        .from('smart_categories')
        .select('name')
        .eq('user_id', user.id);
      
      const existingNames = new Set(existingCategories?.map(c => c.name) || []);
      
      // 3. Filter only new categories
      const newCategories = outlookCats.filter(cat => !existingNames.has(cat.displayName));
      
      if (newCategories.length === 0) {
        toast({ 
          title: 'No new categories', 
          description: 'All Outlook categories are already imported' 
        });
        return;
      }
      
      // 4. Insert all new categories into database
      const categoriesToInsert = newCategories.map(cat => {
        const colorName = getOutlookColorName(cat.color);
        return {
          user_id: user.id,
          name: cat.displayName,
          color: colorName,
          tags: [], // Empty - must be filled by user
          description: 'Imported from Outlook',
          is_active: false // Inactive by default
        };
      });
      
      const { error } = await supabase
        .from('smart_categories')
        .insert(categoriesToInsert);
      
      if (error) {
        toast({
          title: 'Error importing categories',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      
      // 5. Reload categories
      await refresh();
      
      toast({ 
        title: 'Categories imported', 
        description: `${newCategories.length} new categories added. Add tags to activate them.`
      });
      
    } catch (error: any) {
      toast({
        title: 'Error loading categories',
        description: error.message || 'Could not load Outlook categories',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingOutlookCategories(false);
    }
  };

  const getColorHex = (presetColor: string): string => {
    const presetIndex = parseInt(presetColor.replace('preset', ''));
    return OUTLOOK_COLORS[presetIndex]?.hex || '#808080';
  };

  const getOutlookColorName = (presetColor: string): OutlookColor => {
    const presetIndex = parseInt(presetColor.replace('preset', ''));
    return OUTLOOK_COLORS[presetIndex]?.name || 'Blue';
  };

  const startCreate = () => {
    if (categories.length >= 10) {
      toast({ title: 'Limit reached', description: 'You can only create up to 10 categories', variant: 'destructive' });
      return;
    }
    setShowTemplateDialog(true);
  };

  const useTemplate = (template: CategoryTemplate) => {
    setFormData({
      name: template.name,
      color: template.color,
      tags: [...template.tags, '', ''].slice(0, 3),
      description: template.description,
      createInOutlook: false
    });
    setShowTemplateDialog(false);
    setIsCreating(true);
  };

  const useOutlookCategory = (outlookCat: { id: string; displayName: string; color: string }) => {
    const colorName = getOutlookColorName(outlookCat.color);
    
    setFormData({
      name: outlookCat.displayName,
      color: colorName,
      tags: ['', '', ''],
      description: `Category synced from Outlook`,
      createInOutlook: false
    });
    setShowTemplateDialog(false);
    setIsCreating(true);
  };

  const createFromScratch = () => {
    setFormData({ name: '', color: 'Blue', tags: ['', '', ''], description: '', createInOutlook: false });
    setShowTemplateDialog(false);
    setIsCreating(true);
  };

  const startEdit = (category: SmartCategory) => {
    setFormData({
      name: category.name,
      color: category.color,
      tags: [...category.tags, '', '', ''].slice(0, 3),
      description: category.description || '',
      createInOutlook: false
    });
    setEditingId(category.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
    setShowTemplateDialog(false);
  };

  const saveCategory = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Name required', description: 'Please enter a category name', variant: 'destructive' });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({ title: 'Authentication required', description: 'Please log in first', variant: 'destructive' });
      return;
    }

    // If "Create in Outlook" is active, create in Outlook first
    if (formData.createInOutlook) {
      try {
        await GraphService.ensureCategory(formData.name.trim(), formData.color);
        toast({ 
          title: 'Category created in Outlook', 
          description: `"${formData.name}" is now available in your Outlook` 
        });
      } catch (error: any) {
        toast({
          title: 'Outlook creation failed',
          description: error.message || 'Could not create category in Outlook',
          variant: 'destructive',
        });
      }
    }

    const cleanTags = formData.tags.filter(t => t.trim() !== '');
    
    const categoryData = {
      user_id: user.id,
      name: formData.name.trim(),
      color: formData.color,
      tags: cleanTags,
      description: formData.description.trim() || null
    };

    if (editingId) {
      const { error } = await supabase
        .from('smart_categories')
        .update(categoryData)
        .eq('id', editingId);

      if (error) {
        toast({ title: 'Error updating category', description: error.message, variant: 'destructive' });
        return;
      }
      toast({ title: 'Category updated', description: 'Changes saved successfully' });
    } else {
      const { error } = await supabase
        .from('smart_categories')
        .insert([categoryData]);

      if (error) {
        toast({ title: 'Error creating category', description: error.message, variant: 'destructive' });
        return;
      }
      toast({ title: 'Category created', description: 'New category added' });
    }

    setEditingId(null);
    setIsCreating(false);
    refresh();
  };

  const deleteCategory = async (id: string, name: string) => {
    setCategoryToDelete({ id, name });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    const { error } = await supabase
      .from('smart_categories')
      .delete()
      .eq('id', categoryToDelete.id);

    if (error) {
      toast({ title: 'Error deleting category', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Category deleted' });
      refresh();
    }
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  return (
    <div className="space-y-6">
      {/* Enable Smart Categories Toggle */}
      <Card className="backdrop-blur-sm bg-white/50 dark:bg-slate-800/50 border-gray-200/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            📂 Smart Categories Settings
          </CardTitle>
          <CardDescription className="text-xs">
            Enable AI-powered company classification using custom categories. Processing via <strong>Azure OpenAI GPT-4o (West Europe)</strong> with zero data retention.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable-categories" className="text-sm font-medium">
                Enable Smart Categories
              </Label>
              <p className="text-xs text-muted-foreground">
                Allow AI to classify companies based on your custom categories
              </p>
            </div>
            <Switch
              id="enable-categories"
              checked={enableCategories}
              onCheckedChange={toggleCategories}
            />
          </div>

      {enableCategories && (
            <>
              <Separator />
              
              {/* Auto-run Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="auto-run-categories" className="text-sm font-medium">
                    Auto-run Categorization
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Run automatically when enhancing contacts
                  </p>
                </div>
                <Switch
                  id="auto-run-categories"
                  checked={autoRunCategories}
                  onCheckedChange={toggleAutoRun}
                />
              </div>

              <Separator />
              
              <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>Note:</strong> Category is metadata and will always be extracted when Smart Categories is enabled. 
                  It's not a contact field and won't appear in Required Fields settings.
                </AlertDescription>
              </Alert>

              {categories.filter(c => c.is_active).length === 1 && (
                <>
                  <Separator />
                  <Alert className="bg-amber-50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800">
                    <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <AlertDescription className="text-xs text-amber-700 dark:text-amber-300">
                      <strong>Single category active:</strong> The AI will still validate if contacts 
                      match this category. If there's no good match, no category will be assigned.
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Category Management (only if enabled) */}
      {enableCategories && (
        <>
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Create Smart Category
            </DialogTitle>
            <DialogDescription>
              Choose from templates, import from Outlook, or create from scratch.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="templates" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="outlook">From Outlook</TabsTrigger>
              <TabsTrigger value="scratch">From Scratch</TabsTrigger>
            </TabsList>
            
            <TabsContent value="templates" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CATEGORY_TEMPLATES.map((template) => {
                  const colorData = OUTLOOK_COLORS.find(c => c.name === template.color);
                  return (
                    <div
                      key={template.name}
                      className="relative rounded-xl border border-gray-200/50 dark:border-gray-700/50 
                                 bg-white/40 dark:bg-slate-800/40 backdrop-blur-md
                                 p-3 cursor-pointer hover:border-primary/50 transition-all group
                                 shadow-sm hover:shadow-md"
                      onClick={() => useTemplate(template)}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-6 h-6 rounded-full border-2 flex-shrink-0 group-hover:scale-110 transition-transform"
                          style={{ backgroundColor: colorData?.hex }}
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold mb-1 group-hover:text-primary transition-colors">
                            {template.name}
                          </h3>
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {template.tags.map((tag, idx) => (
                              <Badge key={idx} variant="secondary" className="text-[10px] py-0 h-4">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-[10px] text-muted-foreground line-clamp-2">
                            {template.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="outlook" className="mt-4">
              {outlookCategories.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    No Outlook categories loaded yet. Click "Load from Outlook" button first.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {outlookCategories.map((cat) => {
                    const colorHex = getColorHex(cat.color);
                    return (
                      <div
                        key={cat.id}
                        className="relative rounded-xl border border-gray-200/50 dark:border-gray-700/50 
                                   bg-white/40 dark:bg-slate-800/40 backdrop-blur-md
                                   p-3 cursor-pointer hover:border-primary/50 transition-all group
                                   shadow-sm hover:shadow-md"
                        onClick={() => useOutlookCategory(cat)}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-6 h-6 rounded-full border-2 flex-shrink-0 group-hover:scale-110 transition-transform"
                            style={{ backgroundColor: colorHex }}
                          />
                          <h3 className="text-sm font-semibold group-hover:text-primary transition-colors">
                            {cat.displayName}
                          </h3>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="scratch" className="mt-4">
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground mb-4">
                  Start with an empty category and fill in all details yourself.
                </p>
                <Button onClick={createFromScratch}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create from Scratch
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Smart Categories</h2>
          <p className="text-xs text-muted-foreground">
            Define up to 10 custom categories for AI-powered company classification
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={loadOutlookCategories}
            disabled={isLoadingOutlookCategories}
            size="sm"
          >
            {isLoadingOutlookCategories ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Load from Outlook
              </>
            )}
          </Button>
          {!isCreating && categories.length < 10 && (
            <Button onClick={startCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          )}
        </div>
      </div>

      {(isCreating || editingId) && (
        <Card className="glass-card-strong border-primary/50 animate-fade-in-up">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-gradient">{editingId ? 'Edit Category' : 'New Category'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Energy Efficiency"
                maxLength={50}
                className="h-9 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Color</Label>
              <div className="flex flex-wrap gap-1.5">
                {OUTLOOK_COLORS.map(({ name, hex }) => (
                  <button
                    key={name}
                    onClick={() => setFormData({ ...formData, color: name })}
                    className={`w-8 h-8 rounded-lg border-2 transition-all hover-scale ${
                      formData.color === name ? 'border-foreground scale-110 shadow-lg' : 'border-transparent hover:border-muted'
                    }`}
                    style={{ 
                      backgroundColor: hex,
                      boxShadow: formData.color === name ? `0 0 20px ${hex}80` : undefined
                    }}
                    title={name}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Tags (up to 3)</Label>
              {formData.tags.map((tag, idx) => (
                <Input
                  key={idx}
                  value={tag}
                  onChange={(e) => {
                    const newTags = [...formData.tags];
                    newTags[idx] = e.target.value;
                    setFormData({ ...formData, tags: newTags });
                  }}
                  placeholder={`Tag ${idx + 1}`}
                  maxLength={20}
                  className="mb-1.5 h-9 text-sm"
                />
              ))}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Description (optional, max 150 chars)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this category"
                maxLength={150}
                rows={2}
                className="text-sm"
              />
              <p className="text-[10px] text-muted-foreground">{formData.description.length}/150</p>
            </div>

            <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Auto-sync:</strong> If this category doesn't exist in Outlook yet, 
                it will be automatically created when you activate it.
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button onClick={saveCategory} className="h-8 text-sm bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover-lift">
                <Save className="h-3.5 w-3.5 mr-1.5" />
                Save
              </Button>
              <Button variant="outline" onClick={cancelEdit} className="h-8 text-sm hover-scale">
                <X className="h-3.5 w-3.5 mr-1.5" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-center text-muted-foreground">Loading...</p>
      ) : categories.length === 0 ? (
        <Alert>
          <AlertDescription>No categories yet. Create your first category above!</AlertDescription>
        </Alert>
      ) : (
        <div className="grid gap-3">
          {categories.map((cat) => {
            const colorHex = OUTLOOK_COLORS.find(c => c.name === cat.color)?.hex || '#0070C0';
            return (
              <div
                key={cat.id}
                className={`relative rounded-xl border p-3 space-y-2 transition-all
                            bg-white/40 dark:bg-slate-800/40 backdrop-blur-md
                            ${cat.is_active ? 'border-green-500' : 'border-gray-200/50 dark:border-gray-700/50'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold">{cat.name}</h4>
                      {cat.is_active && (
                        <Badge variant="default" className="text-[10px] h-4 py-0">
                          Active
                        </Badge>
                      )}
                      {cat.tags.length === 0 && !cat.is_active && (
                        <Badge variant="outline" className="text-[10px] h-4 py-0 border-yellow-500 text-yellow-600 dark:text-yellow-500">
                          Incomplete
                        </Badge>
                      )}
                    </div>
                    
                    {cat.tags.length === 0 && (
                      <p className="text-xs text-yellow-600 dark:text-yellow-500 mb-1.5">
                        ⚠️ Add tags to activate this category
                      </p>
                    )}
                    
                    {cat.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {cat.tags.map((tag, idx) => (
                          <Badge key={idx} variant="secondary" className="text-[10px] h-4 py-0">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {cat.description && (
                      <p className="text-xs text-muted-foreground">{cat.description}</p>
                    )}
                  </div>

                  <div
                    className="w-6 h-6 rounded-full border-2 flex-shrink-0"
                    style={{ backgroundColor: colorHex }}
                  />
                </div>

              <div className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleCategoryActive(cat.id)}
                  className="h-7 text-xs"
                  disabled={!cat.is_active && cat.tags.length === 0}
                  title={!cat.is_active && cat.tags.length === 0 ? 'Add at least one tag to activate' : ''}
                >
                  {cat.is_active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => startEdit(cat)}
                  className="h-7 text-xs"
                >
                  <Edit2 className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deleteCategory(cat.id, cat.name)}
                  className="h-7 text-xs"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the category "{categoryToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteDialog(false);
              setCategoryToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </>
      )}
    </div>
  );
};
